import { useMemo } from "react";
import {
  CallState,
  type CacheableExternalResource,
  type Response,
} from "@b2bportal/core-types";
import { TRIPS_PATH } from "@b2bportal/core-utilities";
import { Product } from "@b2bportal/purchase-api";
import { QueryStatus } from "@reduxjs/toolkit/query";
import { useActivityTripProduct } from "@tenant/activities";
import { FlightPostBookingHooks } from "@tenant/flights";
import { useHotelTripProduct } from "@tenant/hotels";
import { collectPackageGroundIds, withoutPackageCars } from "./packageCars";
import { useCarTripProduct } from "@/modules/cars-postbooking";
import { useHomesTripProduct } from "@/modules/homes-postbooking";
import { usePackageTripProduct } from "@/modules/packages-postBooking";

const hotelItineraryRoute = (reservationId: string) =>
  `hotels/${reservationId}`;

const flightItineraryRoute = (reservationId: string) =>
  `flights/${reservationId}`;

const carItineraryRoute = (reservationId: string) => `cars/${reservationId}`;

const activityItineraryRoute = (reservationId: string) =>
  `activities/${reservationId}`;

const homesItineraryRoute = (reservationId: string) => `homes/${reservationId}`;

const packageItineraryRoute = (reservationId: string) =>
  `packages/${reservationId}`;

export const useRbcTripProducts = () => {
  const { hotels, pastHotels, hotelReservationRouteProps } =
    useHotelTripProduct({
      tripsPath: TRIPS_PATH,
      reservationPath: hotelItineraryRoute,
    });

  const { flights, pastFlights, flightDetailRouteProps } =
    FlightPostBookingHooks.useFlightTripProduct({
      tripsPath: TRIPS_PATH,
      reservationPath: flightItineraryRoute,
    });

  const { homes, pastHomes, homesReservationRouteProps } = useHomesTripProduct({
    reservationPath: homesItineraryRoute,
  });

  const { cars, pastCars, carReservationRouteProps } = useCarTripProduct({
    tripsPath: TRIPS_PATH,
    reservationPath: carItineraryRoute,
  });

  const { activities, pastActivities, activityReservationRouteProps } =
    useActivityTripProduct({
      tripsPath: TRIPS_PATH,
      reservationPath: activityItineraryRoute,
    });

  const { packages, pastPackages, packageReservationRouteProps } =
    usePackageTripProduct({
      tripsPath: TRIPS_PATH,
      reservationPath: packageItineraryRoute,
    });

  const packageGroundIds = useMemo(
    () =>
      collectPackageGroundIds(
        packages[Product.Package].reservations.data,
        pastPackages[Product.Package].reservations.data
      ),
    [packages, pastPackages]
  );

  const [dedupedCars, dedupedPastCars] = useMemo(() => {
    return [
      {
        [Product.Ground]: {
          ...cars[Product.Ground],
          reservations: withoutPackageCars(
            cars[Product.Ground].reservations,
            packageGroundIds
          ),
        },
      },
      {
        [Product.Ground]: {
          ...pastCars[Product.Ground],
          reservations: withoutPackageCars(
            pastCars[Product.Ground].reservations,
            packageGroundIds
          ),
        },
      },
    ];
  }, [cars, pastCars, packageGroundIds]);

  const allProducts = useMemo(() => {
    const mergeResponses = <T>(
      current: Response<T[]> | CacheableExternalResource<T[]>,
      past: Response<T[]> | CacheableExternalResource<T[]>
    ): Response<T[]> => {
      const resources = [current, past];
      const base = { isUninitialized: false as const, error: undefined };
      if (
        resources.some((resource) =>
          "state" in resource
            ? resource.state === CallState.InProcess
            : resource.isLoading
        )
      ) {
        return {
          ...base,
          status: QueryStatus.pending,
          isLoading: true,
          isError: false,
          isSuccess: false,
        };
      }
      const failed = resources.find((resource) =>
        "state" in resource
          ? resource.state === CallState.Failed
          : resource.isError
      );
      if (failed) {
        return {
          ...base,
          status: QueryStatus.rejected,
          isLoading: false,
          isError: true,
          isSuccess: false,
          error: "error" in failed ? failed.error : undefined,
        };
      }
      return {
        ...base,
        status: QueryStatus.fulfilled,
        isLoading: false,
        isError: false,
        isSuccess: true,
        data: [...(current.data ?? []), ...(past.data ?? [])],
      };
    };

    return {
      [Product.Flight]: {
        ...flights[Product.Flight],
        reservations: mergeResponses(
          flights[Product.Flight].reservations,
          pastFlights[Product.Flight].reservations
        ),
      },
      [Product.Hotel]: {
        ...hotels[Product.Hotel],
        reservations: mergeResponses(
          hotels[Product.Hotel].reservations,
          pastHotels[Product.Hotel].reservations
        ),
      },
      [Product.Homes]: {
        ...homes[Product.Homes],
        reservations: mergeResponses(
          homes[Product.Homes].reservations,
          pastHomes[Product.Homes].reservations
        ),
      },
      [Product.Ground]: {
        ...dedupedCars[Product.Ground],
        reservations: mergeResponses(
          dedupedCars[Product.Ground].reservations,
          dedupedPastCars[Product.Ground].reservations
        ),
      },
      [Product.Activities]: {
        ...activities[Product.Activities],
        reservations: mergeResponses(
          activities[Product.Activities].reservations,
          pastActivities[Product.Activities].reservations
        ),
      },
      [Product.Package]: {
        ...packages[Product.Package],
        reservations: mergeResponses(
          packages[Product.Package].reservations,
          pastPackages[Product.Package].reservations
        ),
      },
    };
  }, [
    flights,
    pastFlights,
    hotels,
    pastHotels,
    homes,
    pastHomes,
    dedupedCars,
    dedupedPastCars,
    activities,
    pastActivities,
    packages,
    pastPackages,
  ]);

  return {
    allProducts,
    pastProducts: {
      ...pastFlights,
      ...pastHotels,
      ...pastHomes,
      ...dedupedPastCars,
      ...pastActivities,
      ...pastPackages,
    },
    hotelReservationRouteProps,
    homesReservationRouteProps,
    flightDetailRouteProps,
    carReservationRouteProps,
    activityReservationRouteProps,
    packageReservationRouteProps,
  };
};
